/**
 * 서비스워커 및 앱 업데이트 상태 관리 (PLAN 14장 & plan/realtime_noti.md).
 *
 * 지원 플랫폼:
 * - iPhone (iOS Safari, 홈 화면 PWA)
 * - Android (Chrome, Samsung Internet, PWA)
 * - Mac / Windows (모든 데스크톱 브라우저)
 * - Android Tablet (Chrome 태블릿)
 *
 * 핵심 메커니즘 및 불변식 (Invariants):
 * 1. 실시간 감지: 30초 주기 폴링 + visibilitychange(화면 복귀 시) + focus + online 시
 *    no-cache `/version.json` 엔드포인트 및 `registration.update()`를 호출한다 (10초 쓰로틀 가드 포함).
 * 2. 엄격한 시맨틱 버전 검증 (isStrictlyNewerVersion):
 *    - 원격 버전이 현재 실행 중인 APP_VERSION보다 엄격하게 높을 때(remote > current)에만 업데이트를 알린다.
 *    - 같은 버전(remote === current)이거나 하위 버전인 경우 절대로 배너를 띄우지 않으며,
 *      WebKit(iOS/Mac Safari) 등의 고스트 대기 워커는 백그라운드에서 조용히 skipWaiting 시키고 정리한다.
 * 3. 승인 시 안전한 업데이트: 사용자가 확인(지금 업데이트)을 누르면 대기 중인 서비스워커에
 *    SKIP_WAITING을 보내고 controllerchange 이벤트 또는 fallback으로 새 번들을 즉시 로드한다.
 */
import { useSyncExternalStore } from 'react';
import { isStrictlyNewerVersion } from '@talk2nick/core';
import { APP_VERSION } from '../auth/buildInfo';

const STORAGE_KEY = 'talk2nick_pending_sw_update';
const STORAGE_VERSION_KEY = 'talk2nick_pending_sw_version';
const CHECK_THROTTLE_MS = 10_000; // 10초 내 중복 네트워크 체크 방지 (데스크톱 창 전환 등)

/** `registerSW`가 돌려주는 활성화 함수. main.tsx가 주입한다. */
let activate: ((reloadPage?: boolean) => Promise<void>) | null = null;
/** 배너의 "닫기"가 호출할 함수 — 알림만 숨기고 대기는 남긴다. */
let dismiss: (() => void) | null = null;
/** 서비스워커 등록 객체 */
let swRegistration: ServiceWorkerRegistration | null = null;

const listeners = new Set<() => void>();

let newVersion: string | null = readPersistedVersion();
let updateAvailable: boolean = readPersisted();

function readPersisted(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const isPending = window.localStorage.getItem(STORAGE_KEY) === '1';
    const storedVer = window.localStorage.getItem(STORAGE_VERSION_KEY);
    // 저장된 버전이 현재 번들 버전보다 높지 않으면 즉시 정리하고 false 반환
    if (!storedVer || !isStrictlyNewerVersion(storedVer, APP_VERSION)) {
      window.localStorage.removeItem(STORAGE_KEY);
      window.localStorage.removeItem(STORAGE_VERSION_KEY);
      return false;
    }
    return isPending;
  } catch {
    return false;
  }
}

function readPersistedVersion(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const ver = window.localStorage.getItem(STORAGE_VERSION_KEY);
    if (!ver || !isStrictlyNewerVersion(ver, APP_VERSION)) {
      window.localStorage.removeItem(STORAGE_VERSION_KEY);
      return null;
    }
    return ver;
  } catch {
    return null;
  }
}

function persist(next: boolean, ver: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (next && ver && isStrictlyNewerVersion(ver, APP_VERSION)) {
      window.localStorage.setItem(STORAGE_KEY, '1');
      window.localStorage.setItem(STORAGE_VERSION_KEY, ver);
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
      window.localStorage.removeItem(STORAGE_VERSION_KEY);
    }
  } catch (error) {
    console.error('[talk2nIcK] 업데이트 대기 상태 저장 실패:', error);
  }
}

function emit(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): boolean {
  return updateAvailable;
}

function getVersionSnapshot(): string | null {
  return newVersion;
}

/**
 * 대기 중인 서비스워커를 백그라운드에서 조용히 활성화(skipWaiting)하여
 * WebKit/iOS 등의 고스트 대기 상태를 해소한다.
 */
function silentlyEvictGhostWorker(): void {
  try {
    if (swRegistration?.waiting) {
      swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
  } catch {
    // ignore
  }
}

/**
 * 새 버전 업데이트가 감지되었을 때 호출된다.
 * 엄격한 시맨틱 버전 검증(remote > current)을 통과한 경우에만 알림을 표시한다.
 */
export function notifyUpdateAvailable(version?: string): void {
  // 버전이 주어지지 않은 경우 (예: Workbox onNeedRefresh 콜백)
  if (!version) {
    // 즉시 배너를 띄우지 않고 /version.json 확인을 먼저 수행한다.
    if (typeof window !== 'undefined') {
      void fetchLatestVersionInfo();
    }
    return;
  }

  // 버전이 제공된 경우 엄격하게 현재 버전보다 높은지 검증
  if (!isStrictlyNewerVersion(version, APP_VERSION)) {
    // 같거나 하위 버전이면 배너를 표시하지 않고 고스트 워커를 정리한다.
    updateAvailable = false;
    newVersion = null;
    persist(false, null);
    silentlyEvictGhostWorker();
    emit();
    return;
  }

  const isNewNotification = !updateAvailable || version !== newVersion;
  updateAvailable = true;
  newVersion = version;
  persist(true, newVersion);

  if (isNewNotification) {
    emit();
  }
}

/**
 * 최신 버전 정보를 /version.json에서 조회하고 검증한다.
 * iOS WebKit Standalone 캐싱 방지를 위해 고유 난수 쿼리 및 no-cache 헤더를 강화한다.
 */
async function fetchLatestVersionInfo(): Promise<string | null> {
  try {
    const bust = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const res = await fetch(`/version.json?_t=${bust}`, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate, max-age=0',
        Pragma: 'no-cache',
      },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { version?: string };
    if (data?.version) {
      if (isStrictlyNewerVersion(data.version, APP_VERSION)) {
        newVersion = data.version;
        updateAvailable = true;
        persist(true, newVersion);
        emit();
        return data.version;
      } else {
        // 원격 버전이 현재 버전과 같거나 낮음 -> 알림 해제 및 고스트 워커 정리
        if (updateAvailable || newVersion) {
          updateAvailable = false;
          newVersion = null;
          persist(false, null);
          emit();
        }
        silentlyEvictGhostWorker();
        return null;
      }
    }
  } catch {
    // 네트워크 실패 시 무시
  }
  return null;
}

/** registerSW가 돌려준 활성화 함수를 보관한다. */
export function setUpdateHandlers(
  activateFn: (reloadPage?: boolean) => Promise<void>,
  dismissFn: () => void,
): void {
  activate = activateFn;
  dismiss = dismissFn;
}

/** 서비스워커 등록 객체를 설정한다 */
export function setRegistration(reg: ServiceWorkerRegistration | undefined): void {
  if (!reg) return;
  swRegistration = reg;
}

let lastCheckTime = 0;

/**
 * 실시간 업데이트 체크를 수행한다.
 * 1. 쓰로틀 가드 (10초)
 * 2. 서비스워커 byte check: registration.update()
 * 3. 정적 엔드포인트 check: /version.json
 *
 * iOS Safari Standalone에서는 SW 등록 객체 주입이 지연될 수 있으므로
 * 필요 시 navigator.serviceWorker.getRegistration()으로 동적 확보하고,
 * SW 업데이트 에러가 /version.json 확인을 차단하지 않도록 격리 병렬 실행한다.
 */
export async function checkForUpdate(force = false): Promise<void> {
  if (typeof window === 'undefined') return;

  const now = Date.now();
  if (!force && now - lastCheckTime < CHECK_THROTTLE_MS) {
    return;
  }
  lastCheckTime = now;

  // swRegistration이 아직 없으면 navigator.serviceWorker에서 탐색
  if (!swRegistration && 'serviceWorker' in navigator) {
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) swRegistration = reg;
    } catch {
      // ignore
    }
  }

  // 1. 서비스워커 바이트 체크 & 2. /version.json 체크를 독립적으로 동시 실행
  const swCheckPromise = (async () => {
    if (swRegistration) {
      try {
        await swRegistration.update();
      } catch (err) {
        console.info('[talk2nIcK] SW update check error:', err);
      }
    }
  })();

  const versionJsonPromise = fetchLatestVersionInfo();

  await Promise.allSettled([swCheckPromise, versionJsonPromise]);
}

let checkerTimer: ReturnType<typeof setInterval> | null = null;
let eventListenersBound = false;

/**
 * 모든 플랫폼(아이폰 Safari/PWA, 안드로이드 PWA, 맥북, 윈도우, 브라우저)에서
 * 실시간 업데이트 알림을 감지할 수 있도록 주기적 폴링 및 이벤트 리스너를 시작한다.
 */
export function setupAutoUpdateChecker(reg?: ServiceWorkerRegistration): void {
  if (reg) setRegistration(reg);
  if (typeof window === 'undefined') return;

  // 앱 실행 즉시 및 1.5초 후 초기 점검
  void checkForUpdate(true);
  setTimeout(() => {
    void checkForUpdate();
  }, 1500);

  // 20초 주기 폴링 (iOS Standalone 유휴 대비 단축)
  if (!checkerTimer) {
    checkerTimer = setInterval(() => {
      void checkForUpdate();
    }, 20_000);
  }

  if (!eventListenersBound) {
    eventListenersBound = true;

    // 모바일 PWA 및 탭 복귀 시 (iOS Safari, Android Chrome 등)
    // 복귀 이벤트는 쓰로틀을 우회한다 (iOS는 백그라운드에서 타이머가 정지되므로 복귀 직후 점검이 핵심)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        void checkForUpdate(true);
      }
    });

    // iOS WebKit Standalone 핵심: 홈 화면 앱 복귀 시 pageshow / resume 이벤트
    window.addEventListener('pageshow', () => {
      void checkForUpdate(true);
    });

    // 윈도우 포커스 시 (데스크톱 맥북, 윈도우 등)
    window.addEventListener('focus', () => {
      void checkForUpdate();
    });

    // 네트워크 복구 시
    window.addEventListener('online', () => {
      void checkForUpdate();
    });

    // iOS PWA 제스처 기반 점검: 사용자가 터치할 때 쓰로틀(10초) 가드를 거쳐 최신 버전 점검
    const handleUserInteraction = () => {
      void checkForUpdate();
    };
    window.addEventListener('touchstart', handleUserInteraction, { passive: true });
    window.addEventListener('pointerdown', handleUserInteraction, { passive: true });

    // 멀티 탭 간 동기화: 다른 탭에서 업데이트 적용 완료 시 배너 닫기
    window.addEventListener('storage', (e) => {
      if (e.key === STORAGE_KEY && e.newValue !== '1') {
        updateAvailable = false;
        newVersion = null;
        emit();
      }
    });
  }
}

/**
 * 사용자가 "지금 업데이트"를 눌렀을 때 호출되어 즉시 새 버전을 적용한다.
 */
export async function applyUpdate(): Promise<void> {
  if (typeof window === 'undefined') return;

  persist(false, null);
  updateAvailable = false;
  emit();

  let reloaded = false;
  const doReload = () => {
    if (reloaded) return;
    reloaded = true;
    window.location.reload();
  };

  // 서비스워커 컨트롤러가 변경되면 즉시 리로드
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      doReload();
    });
  }

  // 대기 중인 워커에 직접 SKIP_WAITING 전송 (WebKit / iOS 안정성 보강)
  silentlyEvictGhostWorker();

  try {
    if (activate) {
      await activate(true);
    }
  } catch (error) {
    console.warn('[talk2nIcK] activate 실행 실패, reload 대체:', error);
  }

  // controllerchange 미발생 환경(Safari Standalone 등) 대비 1.2초 fallback
  setTimeout(() => {
    doReload();
  }, 1200);
}

/** 배너를 닫는다. 대기는 남으므로 다음 시작 때 자동 적용된다. */
export function dismissUpdateBanner(): void {
  if (dismiss) {
    dismiss();
  } else {
    updateAvailable = false;
    emit();
  }
}

/**
 * 앱 시작 시점에 대기 중인 업데이트를 즉시 적용한다.
 * 연속 새로고침 루프를 방지하기 위해 세션당/시간당 가드를 둔다.
 */
export async function applyPendingUpdateOnStart(): Promise<void> {
  if (typeof window === 'undefined') return;

  // 만약 현재 버전과 저장된 버전이 일치하거나 최신 버전이 아니면 즉시 해제
  const storedVer = readPersistedVersion();
  if (!storedVer || !isStrictlyNewerVersion(storedVer, APP_VERSION)) {
    persist(false, null);
    updateAvailable = false;
    newVersion = null;
    silentlyEvictGhostWorker();
    emit();
    return;
  }

  if (!updateAvailable || !activate) return;

  const LAST_RELOAD_KEY = 'talk2nick_last_auto_update_ts';
  try {
    const lastReload = Number(sessionStorage.getItem(LAST_RELOAD_KEY) || '0');
    // 30초 내에 이미 자동 업데이트로 새로고침된 적이 있으면 무한 루프 차단
    if (Date.now() - lastReload < 30_000) {
      console.warn(
        '[talk2nIcK] 자동 업데이트 반복 감지 — 이번 부트에서는 자동 새로고침을 생략합니다.',
      );
      persist(false, null);
      updateAvailable = false;
      newVersion = null;
      emit();
      return;
    }
    sessionStorage.setItem(LAST_RELOAD_KEY, String(Date.now()));
  } catch {
    // sessionStorage 비활성 환경
  }

  try {
    await activate(true);
  } catch (error) {
    console.error('[talk2nIcK] 대기 중인 업데이트 적용 실패:', error);
  }
}

/** 배너에서 대기가 떠 있는지 구독한다. */
export function useUpdateAvailable(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** 감지된 새 버전 정보를 구독한다 (예: "1.1.0") */
export function useNewVersion(): string | null {
  return useSyncExternalStore(subscribe, getVersionSnapshot, getVersionSnapshot);
}

/** 업데이트 상세 정보(가용 여부, 새 버전, 현재 버전)를 반환하는 훅 */
export function useUpdateInfo(): {
  available: boolean;
  newVersion: string | null;
  currentVersion: string;
} {
  const available = useUpdateAvailable();
  const ver = useNewVersion();
  return {
    available,
    newVersion: ver,
    currentVersion: APP_VERSION,
  };
}
